
import React, { useState, useCallback } from 'react';
import { Note } from '@/types/lead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AddNoteForm from './notes/AddNoteForm';
import NoteCard from './notes/NoteCard';
import NoteFilters from './notes/NoteFilters';
import EditNoteDialog from './notes/EditNoteDialog';
import { useToast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface NotesTabContentProps {
  notes: Note[];
  onAddNote: (content: string, tags?: string[]) => Promise<void>;
  onUpdateNote: (noteId: string, updates: { content: string; tags: string[] }) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  availableTags: string[];
}

const NotesTabContent: React.FC<NotesTabContentProps> = ({ 
  notes, 
  onAddNote, 
  onUpdateNote,
  onDeleteNote,
  availableTags 
}) => {
  const { toast } = useToast();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('newest');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  const filteredAndSortedNotes = useCallback(() => {
    let filtered = notes;
    
    // Apply tag filtering
    if (selectedTags.length > 0) {
      filtered = filtered.filter(note => 
        selectedTags.every(tag => note.tags?.includes(tag))
      );
    }
    
    // Apply sorting
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'updated':
          return new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  }, [notes, selectedTags, sortBy]);

  const handleDeleteConfirm = async () => {
    if (!deletingNoteId) return;
    
    try {
      await onDeleteNote(deletingNoteId);
      toast({
        title: "Note Deleted",
        description: "The note has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete note. Please try again.",
      });
    } finally {
      setDeletingNoteId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 md:pb-4">
        <CardTitle className="text-lg md:text-xl">Notes</CardTitle>
        <AddNoteForm onAddNote={onAddNote} availableTags={availableTags} />
      </CardHeader>
      <CardContent>
        <NoteFilters
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          sortBy={sortBy}
          onSortChange={setSortBy}
          availableTags={availableTags}
        />
        
        {filteredAndSortedNotes().length > 0 ? (
          <div className="space-y-3 md:space-y-4">
            {filteredAndSortedNotes().map(note => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={setEditingNote}
                onDelete={setDeletingNoteId}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground p-3 md:p-4 text-xs md:text-sm">
            {notes.length === 0 ? "No notes yet. Add your first note." : "No notes match the selected filters."}
          </p>
        )}
      </CardContent>

      {editingNote && (
        <EditNoteDialog
          note={editingNote}
          isOpen={true}
          setIsOpen={(open) => !open && setEditingNote(null)}
          onSave={onUpdateNote}
          availableTags={availableTags}
        />
      )}

      <AlertDialog open={!!deletingNoteId} onOpenChange={() => setDeletingNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default NotesTabContent;
